import { useWindowSize } from "hooks/useWindowSize";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Filter } from "components/DesignSystem/Filter/Filter";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import {
  SOURCE,
  VENDOR_SEASON_OPTION,
  VENDOR_SOURCE_OPTION,
  VENDOR_TYPE,
  VENDOR_WFORM_OPTION,
} from "constants/vendor";
import { Search } from "components/DesignSystem/Search/Search";
import { VendorFilterProps } from "pages/Books/BillsAndVendors/Vendors";

const SeasonFilter = ({ filter, updateFilter }: VendorFilterProps) => {
  return (
    <div className="t-flex t-flex-col t-gap-3">
      <div className="t-text-body-sm t-text-neutral-80 t-w-full">Season</div>
      <Radio.Root
        onValueChange={(value) => {
          updateFilter("SEASON", value);
        }}
        value={filter.SEASON || ""}
      >
        <Radio.Content>
          {VENDOR_SEASON_OPTION.map((season: string) => (
            <Radio.Item asChild value={season} key={season}>
              {season}
            </Radio.Item>
          ))}
        </Radio.Content>
      </Radio.Root>
    </div>
  );
};

export const WFormFilters = ({ filter, updateFilter }: VendorFilterProps) => {
  return (
    <div className="t-flex t-flex-col t-gap-3">
      <div className="t-text-body-sm t-text-neutral-80 t-w-full">W-Form</div>
      <Radio.Root
        onValueChange={(v) => {
          updateFilter("VENDOR_TYPE", v as keyof typeof VENDOR_TYPE);
        }}
        value={filter.VENDOR_TYPE || ""}
      >
        <Radio.Content>
          {VENDOR_WFORM_OPTION.map(({ label, value }) => (
            <Radio.Item asChild value={value} key={value}>
              {label}
            </Radio.Item>
          ))}
        </Radio.Content>
      </Radio.Root>
      <button
        className="all:unset hover:t-underline t-text-purple t-text-subtext"
        onClick={() => updateFilter("VENDOR_TYPE", null)}
      >
        Reset
      </button>
    </div>
  );
};

const Source = ({ filter, updateFilter }: VendorFilterProps) => {
  return (
    <div className="t-flex t-flex-col t-gap-3">
      <div className="t-text-body-sm t-text-neutral-80 t-w-full">Source</div>
      <Radio.Root
        onValueChange={(v) => {
          updateFilter("SOURCE", v as keyof typeof SOURCE);
        }}
        value={filter.SOURCE || ""}
      >
        <Radio.Content>
          {VENDOR_SOURCE_OPTION.map(({ label, value }) => (
            <Radio.Item asChild value={value} key={value}>
              {label}
            </Radio.Item>
          ))}
        </Radio.Content>
      </Radio.Root>
      <button
        className="all:unset hover:t-underline t-text-purple t-text-subtext"
        onClick={() => updateFilter("SOURCE", null)}
      >
        Reset
      </button>
    </div>
  );
};

const OthersFilter = ({ filter, updateFilter }: VendorFilterProps) => (
  <div className="t-flex t-flex-col t-gap-3">
    <Checkbox
      label={<>Related party</>}
      name="related_party"
      checked={Boolean(filter.RELATED_PARTY)}
      onChange={(e) => updateFilter("RELATED_PARTY", e.target.checked)}
    />
  </div>
);

const AppliedFilters = ({ filter, updateFilter }: VendorFilterProps) => {
  const isShowEstimated1099 = filter.SHOW_ESTIMATED_1099;

  return (
    <>
      {filter.SEASON && (
        <Chip
          isActive
          filterType="SEASON"
          isRemovable={!filter.SHOW_ESTIMATED_1099}
          onRemove={() => updateFilter("SEASON", null)}
        >
          Season {filter.SEASON}
        </Chip>
      )}
      {filter.SOURCE && (
        <Chip
          filterType="SOURCE"
          isActive
          onRemove={() => updateFilter("SOURCE", null)}
        >
          <div className="first-letter:t-uppercase t-lowercase">
            {filter.SOURCE}
          </div>
        </Chip>
      )}
      {isShowEstimated1099 && (
        <Chip
          isFixedFilter
          filterType="W-FORM"
          isActive={Boolean(filter.VENDOR_TYPE)}
          onRemove={() => updateFilter("VENDOR_TYPE", null)}
          onClick={() => updateFilter("VENDOR_TYPE", VENDOR_TYPE.VENDORS)}
        >
          W-Form{" "}
          <div className="t-lowercase">
            {VENDOR_WFORM_OPTION.find(
              ({ value }) => value === filter.VENDOR_TYPE
            )?.label || "required"}
          </div>
        </Chip>
      )}
    </>
  );
};

export const VendorsFilter = ({
  filter,
  updateFilter,
  updateMultipleFilters,
}: VendorFilterProps) => {
  const isShowEstimated1099 = filter.SHOW_ESTIMATED_1099;
  const { isMobile } = useWindowSize();

  const appliedFilterCount = [
    filter.RELATED_PARTY,
    filter.SEASON,
    filter.SOURCE,
    isShowEstimated1099,
  ].filter(Boolean).length;

  return (
    <div className="t-flex t-gap-2 t-w-full t-items-center t-flex-wrap sm:t-flex-nowrap">
      <Search
        className="t-w-1/6 focus:t-w-2/5 t-transition-all sm:t-w-auto sm:t-min-w-0 sm:t-flex-1 sm:focus:t-w-auto"
        placeholder="Search..."
        onChange={(e) => {
          updateMultipleFilters?.({
            SEARCH_TERM: e.target.value,
            PAGE_NUM: 1,
          });
        }}
        defaultValue={filter.SEARCH_TERM || ""}
      />
      <Filter.Root
        defaultValue="SEASON"
        hideDivider={isMobile}
        title={
          isMobile && appliedFilterCount > 0 ? (
            <span className="t-text-text-30">({appliedFilterCount})</span>
          ) : (
            ""
          )
        }
        capsule={
          <span className="t-contents sm:t-hidden">
            <Chip
              onClick={() =>
                updateFilter("RELATED_PARTY", !filter.RELATED_PARTY)
              }
              onRemove={() => updateFilter("RELATED_PARTY", false)}
              isRemovable
              isFixedFilter
              isActive={filter.RELATED_PARTY}
              filterType="OTHERS"
            >
              Related party
            </Chip>
            <AppliedFilters updateFilter={updateFilter} filter={filter} />
          </span>
        }
      >
        <Filter.Portal size="small">
          <Filter.List>
            <Filter.ListItem value="SEASON">Season</Filter.ListItem>
            {isShowEstimated1099 && (
              <Filter.ListItem value="W-FORM">W-Form</Filter.ListItem>
            )}
            <Filter.ListItem value="SOURCE">Source</Filter.ListItem>
            <Filter.ListItem value="OTHERS">Others</Filter.ListItem>
          </Filter.List>
          <Filter.Body value="SEASON">
            <SeasonFilter updateFilter={updateFilter} filter={filter} />
          </Filter.Body>
          <Filter.Body value="W-FORM">
            <WFormFilters updateFilter={updateFilter} filter={filter} />
          </Filter.Body>
          <Filter.Body value="SOURCE">
            <Source updateFilter={updateFilter} filter={filter} />
          </Filter.Body>
          <Filter.Body value="OTHERS">
            <OthersFilter updateFilter={updateFilter} filter={filter} />
          </Filter.Body>
        </Filter.Portal>
      </Filter.Root>
    </div>
  );
};
